import { useEffect, useState } from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ToolInvocationStatus } from '@/lib/chatTypes'

// Libellés FR des tools (fallback : nom brut).
export const TOOL_LABELS: Record<string, string> = {
  navigateTo: 'Navigation vers',
  generateTemplate: 'Génération du template',
  reviseTemplate: 'Modification du template',
  listTemplates: 'Consultation des templates',
}

const ROUTE_LABELS: Record<string, string> = {
  '/suppliers': 'les fournisseurs',
  '/campaigns': 'les campagnes',
  '/settings': 'les paramètres',
  '/agents': 'les agents',
  '/dashboard': 'le tableau de bord',
}

export const SLOW_TOOL_THRESHOLD_MS = 10_000
export const SLOW_TOOL_MESSAGE = 'Ça prend un peu plus de temps…'

export function getToolLabel(name: string, args?: unknown): string {
  const base = TOOL_LABELS[name]
  if (!base) return name
  if (name === 'navigateTo' && typeof args === 'object' && args !== null) {
    const route = (args as { route?: unknown }).route
    if (typeof route === 'string') return `${base} ${ROUTE_LABELS[route] ?? route}`
  }
  return `${base} …`
}

// Retire une ellipse finale (« … » ou « ... ») : l'indicateur running ajoute
// la sienne, jamais deux à la suite.
function stripTrailingEllipsis(text: string): string {
  return text.replace(/\s*(?:…|\.{3})\s*$/, '')
}

export type ToolCallIndicatorProps = {
  name: string
  label?: string
  status: ToolInvocationStatus
  errorMessage?: string
  startedAt?: number
  className?: string
}

export function ToolCallIndicator({
  name,
  label,
  status,
  errorMessage,
  startedAt,
  className,
}: ToolCallIndicatorProps) {
  const [isSlow, setIsSlow] = useState(false)

  useEffect(() => {
    if (status !== 'running') {
      setIsSlow(false)
      return
    }
    const elapsed = startedAt ? Date.now() - startedAt : 0
    const remaining = Math.max(0, SLOW_TOOL_THRESHOLD_MS - elapsed)
    const timer = setTimeout(() => setIsSlow(true), remaining)
    return () => clearTimeout(timer)
  }, [status, startedAt])

  const rawText = label ?? getToolLabel(name)
  const text = status === 'running' ? stripTrailingEllipsis(rawText) : rawText

  return (
    <div
      aria-live="polite"
      data-status={status}
      className={cn(
        'flex items-center gap-2 rounded-md bg-[var(--chat-tool-call-bg)] px-3 py-1.5 text-xs text-slate-700',
        status === 'error' && 'text-red-700',
        className
      )}
    >
      {status === 'running' ? (
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0 motion-safe:animate-spin" />
      ) : status === 'success' ? (
        <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-green-600" />
      ) : (
        <X aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-red-600" />
      )}
      <span>
        {text}
        {status === 'running' && (isSlow ? ` — ${SLOW_TOOL_MESSAGE}` : '…')}
        {status === 'success' && <span className="sr-only"> : terminé</span>}
        {status === 'error' && (errorMessage ? ` : ${errorMessage}` : ' : échec')}
      </span>
    </div>
  )
}
